import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import {
  Box, Paper, Typography, TextField, Button, Grid, Table, TableBody,
  TableCell, TableHead, TableRow, Checkbox,
  Select, MenuItem, FormControl, InputLabel, Alert, Chip,
  Tabs, Tab, IconButton,
  InputAdornment, CircularProgress, Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ProjectChooseDialog from '../components/subcontract/ProjectChooseDialog';
import { useSubcontractProjects } from '../hooks/useSubcontractProjects';
import ActionStatusDialog, { ActionStatusDialogState } from '../components/common/ActionStatusDialog';

// ─────────────────────────────────────────────────────────
// Number-to-words helpers
// ─────────────────────────────────────────────────────────

function numberToWords(n: number): string {
  if (n === 0) return 'Zero';
  if (n < 0) return 'Negative ' + numberToWords(-n);

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const tensArr = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (num: number): string => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) {
      const t = tensArr[Math.floor(num / 10)];
      const o = ones[num % 10];
      return o ? t + ' ' + o : t;
    }
    if (num < 1000) {
      const h = ones[Math.floor(num / 100)] + ' Hundred';
      const remainder = num % 100;
      return remainder ? h + ' & ' + convert(remainder) : h;
    }
    if (num < 1000000) {
      const th = convert(Math.floor(num / 1000)) + ' Thousand';
      const remainder = num % 1000;
      return remainder ? th + ' ' + convert(remainder) : th;
    }
    if (num < 1000000000) {
      const m = convert(Math.floor(num / 1000000)) + ' Million';
      const remainder = num % 1000000;
      return remainder ? m + ' ' + convert(remainder) : m;
    }
    const b = convert(Math.floor(num / 1000000000)) + ' Billion';
    const remainder = num % 1000000000;
    return remainder ? b + ' ' + convert(remainder) : b;
  };

  return convert(Math.floor(n));
}

function kwdToWords(amount: number): string {
  if (isNaN(amount) || amount === 0) return '\u2014';
  const rounded = Math.round(amount * 1000) / 1000;
  const dinars = Math.floor(Math.abs(rounded));
  const fils = Math.round((Math.abs(rounded) - dinars) * 1000);
  let result = 'Kuwaiti Dinars ("' + numberToWords(dinars);
  if (fils > 0) result += ' & Fils ' + numberToWords(fils);
  result += '") only';
  if (rounded < 0) result = 'Negative ' + result;
  return result;
}

// ─────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────

const fmtKWD = (v: number | undefined | null): string => {
  if (v === undefined || v === null || isNaN(v)) return '0.000';
  return v.toFixed(3);
};

const fmtPct = (v: number | undefined | null): string => {
  if (v === undefined || v === null || isNaN(v)) return '0.00%';
  return v.toFixed(2) + '%';
};

const parseNum = (v: string): number => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const today = () => new Date().toISOString().split('T')[0];

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────

interface SubcontractOption {
  id: number;
  subcontract_number: string;
  contract_number: string;
  subcontractor_name: string;
  subcontractor_code: string;
  project_name: string;
  currency: string;
  contract_value: number;
  start_date: string;
  completion_date: string;
  amended_completion_date: string | null;
  performance_bond_required: boolean;
  performance_bond_pct: number;
  retention_pct: number;
  retention_guarantee_required: boolean;
  advance_payment_pct: number;
  advance_payment_guarantee_required: boolean;
  workmens_comp_pct: number;
  workmens_comp_required: boolean;
  all_risk_insurance_pct: number;
  all_risk_insurance_required: boolean;
  tax_clearance_pct: number;
  tax_clearance_required: boolean;
  letter_of_authorization_required: boolean;
  subcontract_agreement_required: boolean;
  civil_id_copies_required: boolean;
  bank_account_details_required: boolean;
  approved_variations: number;
  revised_contract_value: number;
  project_code: string;
  division?: string;
}

interface FADFlag {
  label: string;
  key: string;           // maps to DocumentType in backend
  required: boolean;     // from subcontract settings
  received: boolean;     // from SubcontractDocuments
}

interface PreviousPC {
  work_done_cum: number;
  mos_cum: number;
  deduction_cum: number;
  retention_cum: number;
  adv_payment_recovery_cum: number;
  performance_bond_cum: number;
  advance_guarantee_cum: number;
  workmens_comp_cum: number;
  insurance_cum: number;
  tax_clearance_cum: number;
  material_back_charges_cum: number;
  manpower_back_charges_cum: number;
  equipment_back_charges_cum: number;
  others_cum: number;
  total_deduction_cum: number;
  payment_cum: number;
  executed_work_pct: number;
  executed_work_amt: number;
  mos_pct: number;
  mos_amt: number;
}

interface PCListItem {
  id: number;
  pc_number: string;
  subcontract_number: string;
  subcontract_id: number;
  project_code: string;
  status: string;
  payment_type: string;
  payment_cum: number;
  work_done_up_to: string;
  created_at: string;
}

interface SAPReferenceInfo {
  grpoDocNum: string;
  grpoDocEntry: string;
  invoiceDocNum: string;
  invoiceDocEntry: string;
  goodsIssueDocNum: string;
  goodsIssueDocEntry: string;
}

const emptySAPRefs = (): SAPReferenceInfo => ({
  grpoDocNum: '',
  grpoDocEntry: '',
  invoiceDocNum: '',
  invoiceDocEntry: '',
  goodsIssueDocNum: '',
  goodsIssueDocEntry: '',
});

const boolVal = (v: any): boolean => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const normalized = String(v ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'y' || normalized === 'yes';
};

const numVal = (v: any): number => {
  const n = parseFloat(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const strVal = (v: any): string => String(v ?? '').trim();

const normalizeSubcontractOption = (raw: any, fallbackProjectCode = ''): SubcontractOption => {
  const contractValue = numVal(raw?.contractvalue ?? raw?.subcontractvalue);
  const approvedVariations = numVal(raw?.totalvariations ?? raw?.variationtotal);
  return {
    id: Number(raw?.subcontractid ?? raw?.id ?? 0),
    subcontract_number: strVal(raw?.subcontractnumber ?? raw?.subcontract_number),
    contract_number: strVal(raw?.contractnumber ?? raw?.contract_number ?? raw?.subcontractnumber ?? raw?.subcontract_number),
    subcontractor_name: strVal(raw?.vendorname ?? raw?.subcontractor_name),
    subcontractor_code: strVal(raw?.vendorcode ?? raw?.subcontractor_code),
    project_name: strVal(raw?.projectname ?? raw?.project_name),
    currency: strVal(raw?.currency ?? raw?.subcontractcurrency),
    contract_value: contractValue,
    start_date: strVal(raw?.contractstartdate ?? raw?.startdate ?? raw?.start_date),
    completion_date: strVal(raw?.contractcompletiondate ?? raw?.enddate ?? raw?.completion_date),
    amended_completion_date: raw?.amendedcompletiondate ?? raw?.amended_completion_date ?? null,
    performance_bond_required: boolVal(raw?.performancebondrequired),
    performance_bond_pct: numVal(raw?.performancebondpct),
    retention_pct: numVal(raw?.retentionpct),
    retention_guarantee_required: boolVal(raw?.retentionguaranteerequired),
    advance_payment_pct: numVal(raw?.advancepaymentpct),
    advance_payment_guarantee_required: boolVal(raw?.advancepaymentguaranteerequired),
    workmens_comp_pct: numVal(raw?.workmenscompinsurancepct ?? raw?.workmenscompensationpct),
    workmens_comp_required: boolVal(raw?.workmenscompinsurancerequired ?? raw?.workmenscompensationrequired),
    all_risk_insurance_pct: numVal(raw?.allriskinsurancepct),
    all_risk_insurance_required: boolVal(raw?.allriskinsurancerequired),
    tax_clearance_pct: numVal(raw?.taxclearancecertpct),
    tax_clearance_required: boolVal(raw?.taxclearancecertrequired),
    letter_of_authorization_required: boolVal(raw?.letterofauthorizationrequired),
    subcontract_agreement_required: boolVal(raw?.subcontractagreementrequired),
    civil_id_copies_required: boolVal(raw?.civilidcopiesrequired),
    bank_account_details_required: boolVal(raw?.bankaccountdetailsrequired),
    approved_variations: approvedVariations,
    revised_contract_value: numVal(raw?.revisedcontractvalue) || (contractValue + approvedVariations),
    project_code: strVal(raw?.projectcode ?? raw?.project_code ?? fallbackProjectCode),
    division: strVal(raw?.division ?? raw?.dim1 ?? raw?.lines?.[0]?.dim1),
  };
};

// ─────────────────────────────────────────────────────────
// Styled cell helpers
// ─────────────────────────────────────────────────────────

const cellSx = { px: 1, py: 0.5, fontSize: '0.82rem', border: '1px solid #ccc' };
const headCellSx = { ...cellSx, fontWeight: 700, backgroundColor: '#e3f2fd', textAlign: 'center' as const };
const computedCellSx = { ...cellSx, backgroundColor: '#f5f5f5', textAlign: 'right' as const };
const inputCellSx = { ...cellSx, p: 0 };
const rightCellSx = { ...cellSx, textAlign: 'right' as const };

const CellInput: React.FC<{
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
}> = ({ value, onChange, disabled, min, max }) => (
  <TextField
    type="number"
    size="small"
    fullWidth
    value={value === 0 ? '' : value}
    disabled={disabled}
    onChange={(e) => {
      let v = parseNum(e.target.value);
      if (min !== undefined && v < min) v = min;
      if (max !== undefined && v > max) v = max;
      onChange(v);
    }}
    inputProps={{ style: { textAlign: 'right', fontSize: '0.82rem', padding: '6px 8px' }, step: '0.001' }}
    sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
  />
);

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

const PaymentCertificate: React.FC = () => {
  const dispatch = useDispatch();
  const { companyId: selectedCompanyId, projects: projectOptions, loading: projectOptionsLoading } = useSubcontractProjects();
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // ── List tab state ──
  const [pcList, setPcList] = useState<PCListItem[]>([]);
  const [pcListLoading, setPcListLoading] = useState(false);
  const [pcListFilter, setPcListFilter] = useState<string>('All');
  const [pcSearchQuery, setPcSearchQuery] = useState('');

  // ── Form: selection ──
  const [subcontractOptions, setSubcontractOptions] = useState<SubcontractOption[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedSubcontract, setSelectedSubcontract] = useState<SubcontractOption | null>(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  // subcontractPickerOpen reserved for future use

  // ── Form: loading / saving ──
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionStatus, setActionStatus] = useState<ActionStatusDialogState>({
    open: false,
    status: 'processing',
    title: '',
    message: '',
  });
  const [editingPCId, setEditingPCId] = useState<number | null>(null);
  const [pcStatus, setPcStatus] = useState<string>('Draft');
  const isReadOnly = pcStatus === 'SubmittedToFinance' || pcStatus === 'Approved';
  const [sapRefs, setSapRefs] = useState<SAPReferenceInfo>(emptySAPRefs);

  // ── Header fields ──
  const [workDoneUpTo, setWorkDoneUpTo] = useState(today());
  const [paymentNo, setPaymentNo] = useState('PC-1');
  const [paymentType, setPaymentType] = useState('Interim');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());

  // ── Section 3: Work Done (computed from Section 5) ──

  // ── Section 4: FAD ──
  const [fadFlags, setFadFlags] = useState<FADFlag[]>([]);

  // ── Total work done from SubcontractWorkDone (cap for Work Done A) ──
  const [maxWorkDoneFromEntries, setMaxWorkDoneFromEntries] = useState(0);

  // ── Section 5: Finance evaluation ──
  const [workDoneCum, setWorkDoneCum] = useState(0);
  const [mosCum, setMosCum] = useState(0);
  const [deductionCum, setDeductionCum] = useState(0);
  const [materialBackChargesCum, setMaterialBackChargesCum] = useState(0);
  const [manpowerBackChargesCum, setManpowerBackChargesCum] = useState(0);
  const [equipmentBackChargesCum, setEquipmentBackChargesCum] = useState(0);
  const [othersCum, setOthersCum] = useState(0);

  // ── Previous PC ──
  const [prevPC, setPrevPC] = useState<PreviousPC>({
    work_done_cum: 0, mos_cum: 0, deduction_cum: 0, retention_cum: 0,
    adv_payment_recovery_cum: 0, performance_bond_cum: 0, advance_guarantee_cum: 0,
    workmens_comp_cum: 0, insurance_cum: 0, tax_clearance_cum: 0,
    material_back_charges_cum: 0, manpower_back_charges_cum: 0,
    equipment_back_charges_cum: 0, others_cum: 0, total_deduction_cum: 0,
    payment_cum: 0, executed_work_pct: 0, executed_work_amt: 0,
    mos_pct: 0, mos_amt: 0,
  });

  // ── Print ref ──
  const printRef = useRef<HTMLDivElement>(null);

  // ─────────────────────────────────────────────────────────
  // Derived calculations from subcontract
  // ─────────────────────────────────────────────────────────

  const sc = selectedSubcontract;
  const originalValue = sc?.contract_value ?? 0;
  const approvedVariations = sc?.approved_variations ?? 0;
  const totalContractValue = sc?.revised_contract_value ?? (originalValue + approvedVariations);
  const retentionPct = sc?.retention_pct ?? 0;
  const advPaymentPct = sc?.advance_payment_pct ?? 0;
  const performanceBondPct = sc?.performance_bond_pct ?? 0;
  const workmensCompPct = sc?.workmens_comp_pct ?? 0;
  const allRiskInsurancePct = sc?.all_risk_insurance_pct ?? 0;
  const taxClearancePct = sc?.tax_clearance_pct ?? 0;

  // ─────────────────────────────────────────────────────────
  // FAD-based deduction rules:
  // Deduction only applies when document is REQUIRED and NOT YET RECEIVED.
  // If not required, or if required and received → no deduction (pct = 0).
  // When fadFlags haven't loaded yet, fall back to subcontract required flags
  // and conservatively assume documents are NOT received (apply deduction).
  // ─────────────────────────────────────────────────────────
  const fadRequiredFallback: Record<string, boolean> = useMemo(() => ({
    PerformanceBond: sc?.performance_bond_required ?? false,
    AdvancePaymentGuarantee: sc?.advance_payment_guarantee_required ?? false,
    WorkmensCompInsurance: sc?.workmens_comp_required ?? false,
    AllRiskInsurance: sc?.all_risk_insurance_required ?? false,
    TaxClearanceCert: sc?.tax_clearance_required ?? false,
  }), [sc]);

  const isFadRequiredAndNotReceived = useCallback((key: string) => {
    const flag = fadFlags.find(f => f.key === key);
    if (flag) return flag.required && !flag.received;
    // FAD flags not loaded yet — fall back to subcontract required flags,
    // assume document NOT received (conservatively apply deduction)
    return fadRequiredFallback[key] ?? false;
  }, [fadFlags, fadRequiredFallback]);

  const effectivePerformanceBondPct = isFadRequiredAndNotReceived('PerformanceBond') ? performanceBondPct : 0;
  const effectiveAdvanceGuaranteePct = isFadRequiredAndNotReceived('AdvancePaymentGuarantee') ? advPaymentPct : 0;
  const effectiveWorkmensCompPct = isFadRequiredAndNotReceived('WorkmensCompInsurance') ? workmensCompPct : 0;
  const effectiveAllRiskInsurancePct = isFadRequiredAndNotReceived('AllRiskInsurance') ? allRiskInsurancePct : 0;
  const effectiveTaxClearancePct = isFadRequiredAndNotReceived('TaxClearanceCert') ? taxClearancePct : 0;

  // ─────────────────────────────────────────────────────────
  // Section 3 computed from Section 5 (Work Done A = Executed Work, MOS = MOS)
  // ─────────────────────────────────────────────────────────

  const executedWorkAmt = workDoneCum;
  const executedWorkPct = totalContractValue > 0 ? (workDoneCum / totalContractValue) * 100 : 0;
  const mosAmt = mosCum;
  const mosPct = totalContractValue > 0 ? (mosCum / totalContractValue) * 100 : 0;

  // ─────────────────────────────────────────────────────────
  // Section 5 computed values
  // ─────────────────────────────────────────────────────────

  const retentionCum = useMemo(() => (workDoneCum * retentionPct) / 100, [workDoneCum, retentionPct]);
  const advPaymentRecoveryCum = useMemo(() => (workDoneCum * advPaymentPct) / 100, [workDoneCum, advPaymentPct]);
  const currentWorkForGuarantees = Math.max(0, workDoneCum - prevPC.work_done_cum);
  // Document-based guarantees: while the FAD document is required and missing, keep
  // withholding on the current period's work. Once the document is received (or not
  // required), the cumulative resets to 0 so the Net column (cum - previous) refunds
  // everything previously withheld back to the subcontractor in this PC; subsequent
  // PCs then carry 0 cumulative / 0 previous / 0 net for that line.
  const performanceBondCum = isFadRequiredAndNotReceived('PerformanceBond')
    ? prevPC.performance_bond_cum + (currentWorkForGuarantees * performanceBondPct) / 100
    : 0;
  const advanceGuaranteeCum = isFadRequiredAndNotReceived('AdvancePaymentGuarantee')
    ? prevPC.advance_guarantee_cum + (currentWorkForGuarantees * advPaymentPct) / 100
    : 0;
  const workmensCompCum = isFadRequiredAndNotReceived('WorkmensCompInsurance')
    ? prevPC.workmens_comp_cum + (currentWorkForGuarantees * workmensCompPct) / 100
    : 0;
  const insuranceCum = isFadRequiredAndNotReceived('AllRiskInsurance')
    ? prevPC.insurance_cum + (currentWorkForGuarantees * allRiskInsurancePct) / 100
    : 0;
  const taxClearanceCum = isFadRequiredAndNotReceived('TaxClearanceCert')
    ? prevPC.tax_clearance_cum + (currentWorkForGuarantees * taxClearancePct) / 100
    : 0;

  const totalDeductionCum = useMemo(() =>
    mosCum + deductionCum + retentionCum + advPaymentRecoveryCum +
    performanceBondCum + advanceGuaranteeCum +
    workmensCompCum + insuranceCum + taxClearanceCum + materialBackChargesCum +
    manpowerBackChargesCum + equipmentBackChargesCum + othersCum,
    [mosCum, deductionCum, retentionCum, advPaymentRecoveryCum, performanceBondCum, advanceGuaranteeCum, workmensCompCum,
      insuranceCum, taxClearanceCum, materialBackChargesCum, manpowerBackChargesCum, equipmentBackChargesCum, othersCum]);

  const paymentCum = useMemo(() => workDoneCum - totalDeductionCum, [workDoneCum, totalDeductionCum]);

  // Previous columns
  const retentionPrev = prevPC.retention_cum;
  const advPaymentRecoveryPrev = prevPC.adv_payment_recovery_cum;
  const performanceBondPrev = prevPC.performance_bond_cum;
  const advanceGuaranteePrev = prevPC.advance_guarantee_cum;
  const workmensCompPrev = prevPC.workmens_comp_cum;
  const insurancePrev = prevPC.insurance_cum;
  const taxClearancePrev = prevPC.tax_clearance_cum || 0;
  const totalDeductionPrev = prevPC.total_deduction_cum;
  const paymentPrev = prevPC.payment_cum;

  // Net columns
  const workDoneNet = workDoneCum - prevPC.work_done_cum;
  const mosNet = mosCum - prevPC.mos_cum;
  const deductionNet = deductionCum - prevPC.deduction_cum;
  const retentionNet = retentionCum - retentionPrev;
  const advPaymentRecoveryNet = advPaymentRecoveryCum - advPaymentRecoveryPrev;
  const performanceBondNet = performanceBondCum - performanceBondPrev;
  const advanceGuaranteeNet = advanceGuaranteeCum - advanceGuaranteePrev;
  const workmensCompNet = workmensCompCum - workmensCompPrev;
  const insuranceNet = insuranceCum - insurancePrev;
  const taxClearanceNet = taxClearanceCum - taxClearancePrev;
  const materialBackChargesNet = materialBackChargesCum - prevPC.material_back_charges_cum;
  const manpowerBackChargesNet = manpowerBackChargesCum - prevPC.manpower_back_charges_cum;
  const equipmentBackChargesNet = equipmentBackChargesCum - prevPC.equipment_back_charges_cum;
  const othersNet = othersCum - prevPC.others_cum;
  const totalDeductionNet = totalDeductionCum - totalDeductionPrev;
  const paymentNet = paymentCum - paymentPrev;

  // ─────────────────────────────────────────────────────────
  // Payment type logic
  // ─────────────────────────────────────────────────────────

  const isAdvanced = paymentType === 'Advanced';
  const advancePaymentValue = (totalContractValue * advPaymentPct) / 100;

  // For Advanced type, the net payment is the advance payment value
  // For Interim type, it's the calculated paymentNet
  const finalPaymentNet = isAdvanced ? advancePaymentValue : paymentNet;
  const paymentWords = useMemo(() => kwdToWords(finalPaymentNet), [finalPaymentNet]);

  // ─────────────────────────────────────────────────────────
  // Fetch subcontracts when project changes
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedProject) { setSubcontractOptions([]); return; }
    const fetchSubcontracts = async () => {
      try {
        const params: any = { projectCode: selectedProject };
        if (selectedCompanyId) params.companyId = selectedCompanyId;
        const res: any = await apiService.listSubcontracts(params);
        const list = res?.data || res || [];
        setSubcontractOptions((Array.isArray(list) ? list : []).map((s: any) => normalizeSubcontractOption(s, selectedProject)));
      } catch {
        dispatch(showSnackbar({ message: 'Failed to load subcontracts', severity: 'error' }));
      }
    };
    fetchSubcontracts();
  }, [selectedProject, selectedCompanyId, dispatch]);

  // ─────────────────────────────────────────────────────────
  // Load previous PC data & next payment no when subcontract selected
  // ─────────────────────────────────────────────────────────

  const loadSubcontractData = useCallback(async (sc: SubcontractOption) => {
    setSelectedSubcontract(sc);
    setLoading(true);
    try {
      // Fetch all PCs for this subcontract (nested route)
      let pcList: any[] = [];
      try {
        const pcRes: any = await apiService.get(`/subcontract/${sc.id}/payment-certificates`);
        pcList = Array.isArray(pcRes) ? pcRes : (pcRes?.data || []);
      } catch {
        pcList = [];
      }

      // Derive previous PC data from the last *Interim* PC in the list.
      // Advanced PCs are excluded — their value is recovered via the
      // "Adv Payment Recovery" deduction line, not carried forward as previous cumulative.
      const paymentSequence = (pc: any) => {
        const match = String(pc.paymentnumber || pc.payment_no || pc.pc_number || '').match(/^PC-(\d+)$/i);
        return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
      };
      const interimPCs = pcList
        .filter((pc: any) => String(pc.paymenttype || '').toLowerCase() !== 'advanced')
        .sort((a: any, b: any) => (
          paymentSequence(a) - paymentSequence(b)
          || Number(a.paymentcertificateid || a.id || 0) - Number(b.paymentcertificateid || b.id || 0)
        ));
      const prev: any = interimPCs.length > 0 ? interimPCs[interimPCs.length - 1] : {};
      const pf = (v: any) => parseFloat(v || 0);

      // For PCs saved before deduction columns existed, recalculate from grossvaluation + subcontract %
      // IMPORTANT: Use != null check, NOT ||, because 0 is a valid value
      // (e.g. when a document like Workmen's Comp cert is received, cumulative becomes 0 intentionally)
      const prevGross = pf(prev.grossvaluation);
      const performanceBondCumPrev = prev.performancebondamt != null ? pf(prev.performancebondamt) : 0;
      const advanceGuaranteeCumPrev = prev.advanceguaranteeamt != null ? pf(prev.advanceguaranteeamt) : 0;
      const workmensCompCumPrev = prev.workmenscompamt != null ? pf(prev.workmenscompamt) : (prevGross * (sc.workmens_comp_pct || 0) / 100);
      const insuranceCumPrev = prev.insuranceamt != null ? pf(prev.insuranceamt) : (prevGross * (sc.all_risk_insurance_pct || 0) / 100);
      const taxClearanceCumPrev = prev.taxclearanceamt != null ? pf(prev.taxclearanceamt) : (prevGross * (sc.tax_clearance_pct || 0) / 100);

      setPrevPC({
        work_done_cum: pf(prev.cumulativeworkdoneamt),
        mos_cum: pf(prev.materialsonsiteamt),
        deduction_cum: pf(prev.deductioncum),
        retention_cum: pf(prev.retentionamt),
        adv_payment_recovery_cum: pf(prev.advancerecoveryamt),
        performance_bond_cum: performanceBondCumPrev,
        advance_guarantee_cum: advanceGuaranteeCumPrev,
        workmens_comp_cum: workmensCompCumPrev,
        insurance_cum: insuranceCumPrev,
        tax_clearance_cum: taxClearanceCumPrev,
        material_back_charges_cum: pf(prev.materialbackchargesamt),
        manpower_back_charges_cum: pf(prev.manpowerbackchargesamt),
        equipment_back_charges_cum: pf(prev.equipmentbackchargesamt),
        others_cum: pf(prev.othersamt),
        total_deduction_cum: pf(prev.cumulativeworkdoneamt) - pf(prev.netpayable),
        payment_cum: pf(prev.netpayable),
        executed_work_pct: pf(prev.cumulativeworkdonepct),
        executed_work_amt: pf(prev.cumulativeworkdoneamt),
        mos_pct: pf(prev.materialsonsitepct),
        mos_amt: pf(prev.materialsonsiteamt),
      });

      // Fetch total work done for this subcontract = latest cumulative entry (each record is already cumulative)
      let totalWorkDone = 0;
      let latestGRPORef = emptySAPRefs();
      try {
        const wdRes: any = await apiService.get(`/subcontract/${sc.id}/work-done`);
        const wdList = Array.isArray(wdRes) ? wdRes : (wdRes?.data || []);
        // Each entry is cumulative, so take the maximum (latest) value
        totalWorkDone = wdList.reduce((max: number, wd: any) => {
          const val = parseFloat(wd.cumulativeworkdone || wd.cumulative_work_done || 0);
          return val > max ? val : max;
        }, 0);
        const latestPostedGRPO = [...wdList]
          .filter((wd: any) => Number(wd.sapgrpodocentry || 0) > 0 || Number(wd.sapgrpodocnum || 0) > 0)
          .sort((a: any, b: any) => Number(b.workdoneid || b.id || 0) - Number(a.workdoneid || a.id || 0))[0];
        if (latestPostedGRPO) {
          latestGRPORef = {
            grpoDocNum: String(latestPostedGRPO.sapgrpodocnum || ''),
            grpoDocEntry: String(latestPostedGRPO.sapgrpodocentry || ''),
            invoiceDocNum: '',
            invoiceDocEntry: '',
            goodsIssueDocNum: '',
            goodsIssueDocEntry: '',
          };
        }
      } catch {
        // If work done fetch fails, default to 0
      }
      setSapRefs(latestGRPORef);
      setMaxWorkDoneFromEntries(totalWorkDone);

      // Default Work Done (A) cumulative = total work done from all entries
      // The "Net" column will automatically be cumulative - previous
      setWorkDoneCum(totalWorkDone);
      setMosCum(pf(prev.materialsonsiteamt));
      setDeductionCum(pf(prev.deductioncum));
      setMaterialBackChargesCum(pf(prev.materialbackchargesamt));
      setManpowerBackChargesCum(pf(prev.manpowerbackchargesamt));
      setEquipmentBackChargesCum(pf(prev.equipmentbackchargesamt));
      setOthersCum(pf(prev.othersamt));

      // Section 3 is now computed from Section 5 (workDoneCum, mosCum)

      // Derive the next number from the greatest numeric suffix; list length is unsafe after deletions.
      const pcCount = pcList.length;
      const highestPaymentSequence = pcList.reduce((highest: number, pc: any) => {
        const sequence = paymentSequence(pc);
        return Number.isSafeInteger(sequence) ? Math.max(highest, sequence) : highest;
      }, 0);
      const nextNum = `PC-${highestPaymentSequence + 1}`;
      setPaymentNo(nextNum);

      // Determine payment type
      if (pcCount === 0 && sc.advance_payment_pct > 0) {
        setPaymentType('Advanced');
      } else {
        setPaymentType('Interim');
      }

      // Load FAD flags from subcontract details (required flags + document received status)
      try {
        const scDetail: any = await apiService.getSubcontract(sc.id);
        const scData = scDetail?.data || scDetail;
        const docs: any[] = scData?.documents || [];

        // Build FAD flags from subcontract required settings + received status
        const docTypeDefs = [
          { label: 'Letter of Authorization', key: 'LetterOfAuthorization', requiredField: 'letterofauthorizationrequired' },
          { label: 'Performance Bond', key: 'PerformanceBond', requiredField: 'performancebondrequired' },
          { label: 'All Risk Insurance', key: 'AllRiskInsurance', requiredField: 'allriskinsurancerequired' },
          { label: 'Tax Clearance Certificate', key: 'TaxClearanceCert', requiredField: 'taxclearancecertrequired' },
          { label: 'Advance Payment Guarantee', key: 'AdvancePaymentGuarantee', requiredField: 'advancepaymentguaranteerequired' },
          { label: "Workmen's Compensation Insurance", key: 'WorkmensCompInsurance', requiredField: 'workmenscompinsurancerequired' },
          { label: 'Retention Guarantee', key: 'RetentionGuarantee', requiredField: 'retentionguaranteerequired' },
        ];

        const flags: FADFlag[] = docTypeDefs.map((def) => {
          const docRow = docs.find((d: any) => (d.documenttype || '').toLowerCase() === def.key.toLowerCase());
          return {
            label: def.label,
            key: def.key,
            required: !!(scData[def.requiredField]),
            received: !!(docRow?.received),
          };
        });
        setFadFlags(flags);
        setSelectedSubcontract(normalizeSubcontractOption(scData, sc.project_code));
      } catch {
        // FAD flags remain empty
      }
    } catch (err: any) {
      dispatch(showSnackbar({ message: 'Failed to load subcontract data: ' + (err?.message || ''), severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // ─────────────────────────────────────────────────────────
  // Load PC list
  // ─────────────────────────────────────────────────────────

  const loadPCList = useCallback(async () => {
    setPcListLoading(true);
    try {
      // If no subcontract options loaded yet, fetch all subcontracts
      let scList = subcontractOptions;
      if (scList.length === 0) {
        try {
          const selectedCompanyId = localStorage.getItem('selectedCompanyId');
          const params: any = {};
          if (selectedCompanyId) params.companyId = Number(selectedCompanyId);
          const res: any = await apiService.listSubcontracts(params);
          const raw = Array.isArray(res) ? res : (res?.data || []);
          scList = raw.map((s: any) => normalizeSubcontractOption(s));
        } catch {
          scList = [];
        }
      }

      // Fetch PCs from all subcontracts
      const allPCs: any[] = [];
      await Promise.all(scList.map(async (sc) => {
        try {
          const res: any = await apiService.get(`/subcontract/${sc.id}/payment-certificates`);
          const list = Array.isArray(res) ? res : (res?.data || []);
          list.forEach((pc: any) => {
            allPCs.push({ ...pc, _subcontract_number: sc.subcontract_number || '', _project_code: sc.project_code || '', _subcontract_id: sc.id });
          });
        } catch {
          // Skip subcontracts with no PCs
        }
      }));

      // Apply filters
      let filtered = allPCs;
      if (pcListFilter !== 'All') {
        filtered = filtered.filter((pc) => (pc.pcstatus || pc.status || '').toLowerCase() === pcListFilter.toLowerCase());
      }
      if (pcSearchQuery.trim()) {
        const q = pcSearchQuery.trim().toLowerCase();
        filtered = filtered.filter((pc) =>
          (pc.paymentnumber || pc.pc_number || '').toLowerCase().includes(q) ||
          (pc._subcontract_number || '').toLowerCase().includes(q)
        );
      }

      setPcList(filtered.map((pc: any) => ({
        id: pc.paymentcertificateid || pc.id,
        pc_number: pc.paymentnumber || pc.pc_number || pc.payment_no || '',
        subcontract_number: pc._subcontract_number || pc.subcontract_number || '',
        subcontract_id: pc._subcontract_id || pc.subcontractid || pc.subcontract_id,
        project_code: pc._project_code || pc.project_code || '',
        status: pc.pcstatus || pc.status || '',
        payment_type: pc.paymenttype || pc.payment_type || '',
        payment_cum: parseFloat(pc.paymentcum || pc.payment_net || pc.payment_cum || 0),
        work_done_up_to: pc.workdoneupto || pc.work_done_up_to || '',
        created_at: pc.createddate || pc.created_at || '',
      })));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to load payment certificates', severity: 'error' }));
    } finally {
      setPcListLoading(false);
    }
  }, [pcListFilter, pcSearchQuery, subcontractOptions, dispatch]);

  useEffect(() => {
    if (activeTab === 1) loadPCList();
  }, [activeTab, loadPCList]);

  // ─────────────────────────────────────────────────────────
  // Load existing PC for edit
  // ─────────────────────────────────────────────────────────

  const loadExistingPC = useCallback(async (pcId: number, subcontractId?: number) => {
    setLoading(true);
    try {
      // Use subcontractId if provided, otherwise try to find it from pcList
      const scId = subcontractId || pcList.find((p) => p.id === pcId)?.subcontract_id;
      if (!scId) {
        dispatch(showSnackbar({ message: 'Cannot determine subcontract for this PC', severity: 'error' }));
        setLoading(false);
        return;
      }
      const res: any = await apiService.get(`/subcontract/${scId}/payment-certificates/${pcId}`);
      const pc = res?.data || res;

      setEditingPCId(pc.paymentcertificateid || pc.id);
      setPcStatus(pc.pcstatus || pc.status || 'Draft');
      setSapRefs({
        grpoDocNum: String(pc.sapgrpodocnum || ''),
        grpoDocEntry: String(pc.sapgrpodocentry || ''),
        invoiceDocNum: String(pc.sapinvoicedocnum || ''),
        invoiceDocEntry: String(pc.sapinvoicedocentry || ''),
        goodsIssueDocNum: String(pc.sapgoodsissuedocnum || ''),
        goodsIssueDocEntry: String(pc.sapgoodsissuedocentry || ''),
      });
      setSelectedProject(pc.projectcode || pc.project_code || '');
      setPaymentNo(pc.paymentnumber || pc.payment_no || pc.pc_number || '');
      setPaymentType(pc.paymenttype || pc.payment_type || 'Interim');
      setWorkDoneUpTo(pc.workdoneupto || pc.work_done_up_to || today());
      setInvoiceNo(pc.invoiceno || pc.invoice_no || '');
      setInvoiceDate(pc.invoicedate || pc.invoice_date || today());

      // Load the subcontract
      const pcSubcontractId = pc.subcontractid || pc.subcontract_id || scId;
      if (pcSubcontractId) {
        try {
          const scRes: any = await apiService.getSubcontract(pcSubcontractId);
          const scData = scRes?.data || scRes;
          setSelectedSubcontract(normalizeSubcontractOption(scData));
        } catch {
          // If subcontract load fails, continue with saved data
        }
        if (!pc.sapgrpodocentry && !pc.sapgrpodocnum) {
          try {
            const wdRes: any = await apiService.get(`/subcontract/${pcSubcontractId}/work-done`);
            const wdList = Array.isArray(wdRes) ? wdRes : (wdRes?.data || []);
            const latestPostedGRPO = [...wdList]
              .filter((wd: any) => Number(wd.sapgrpodocentry || 0) > 0 || Number(wd.sapgrpodocnum || 0) > 0)
              .sort((a: any, b: any) => Number(b.workdoneid || b.id || 0) - Number(a.workdoneid || a.id || 0))[0];
            if (latestPostedGRPO) {
              setSapRefs((prev) => ({
                ...prev,
                grpoDocNum: String(latestPostedGRPO.sapgrpodocnum || ''),
                grpoDocEntry: String(latestPostedGRPO.sapgrpodocentry || ''),
              }));
            }
          } catch {
            // Keep existing refs if work done lookup fails
          }
        }
      }

      // Derive previous PC values from the "Previous*" columns stored in the current PC record
      setPrevPC({
        work_done_cum: parseFloat(pc.previouscumulativeworkdoneamt || 0),
        mos_cum: parseFloat(pc.previousmaterialsonsiteamt || 0),
        deduction_cum: 0,
        retention_cum: parseFloat(pc.previousretentionamt || 0),
        adv_payment_recovery_cum: parseFloat(pc.previousadvancerecoveryamt || 0),
        performance_bond_cum: parseFloat(pc.previousperformancebondamt || 0),
        advance_guarantee_cum: parseFloat(pc.previousadvanceguaranteeamt || 0),
        workmens_comp_cum: parseFloat(pc.previousworkmenscompamt || 0),
        insurance_cum: parseFloat(pc.previousinsuranceamt || 0),
        tax_clearance_cum: parseFloat(pc.previoustaxclearanceamt || 0),
        material_back_charges_cum: parseFloat(pc.previousmaterialbackchargesamt || 0),
        manpower_back_charges_cum: parseFloat(pc.previousmanpowerbackchargesamt || 0),
        equipment_back_charges_cum: parseFloat(pc.previousequipmentbackchargesamt || 0),
        others_cum: parseFloat(pc.previousothersamt || 0),
        total_deduction_cum: 0,
        payment_cum: parseFloat(pc.previousnetpayable || 0),
        executed_work_pct: 0,
        executed_work_amt: parseFloat(pc.previouscumulativeworkdoneamt || 0),
        mos_pct: 0,
        mos_amt: parseFloat(pc.previousmaterialsonsiteamt || 0),
      });

      // Cumulative fields — map from DB column names (lowercased)
      // Note: retentionCum, advPaymentRecoveryCum, workmensCompCum, insuranceCum are computed via useMemo
      setWorkDoneCum(parseFloat(pc.cumulativeworkdoneamt || 0));
      setMosCum(parseFloat(pc.materialsonsiteamt || 0));
      setDeductionCum(parseFloat(pc.deductioncum || 0));
      setMaterialBackChargesCum(parseFloat(pc.materialbackchargesamt || 0));
      setManpowerBackChargesCum(parseFloat(pc.manpowerbackchargesamt || 0));
      setEquipmentBackChargesCum(parseFloat(pc.equipmentbackchargesamt || 0));
      setOthersCum(parseFloat(pc.othersamt || 0));

      // Section 3 is computed from Section 5 (workDoneCum, mosCum)

      // FAD flags — DB column is FADFlags, lowercased to fadflags
      const loadedFlags = pc.fadflags || pc.fad_flags;
      if (loadedFlags && Array.isArray(loadedFlags)) {
        setFadFlags(loadedFlags);
      }

      setActiveTab(0);
    } catch (err: any) {
      dispatch(showSnackbar({ message: 'Failed to load payment certificate: ' + (err?.message || ''), severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, pcList]);

  // ─────────────────────────────────────────────────────────
  // Reset form
  // ─────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setEditingPCId(null);
    setPcStatus('Draft');
    setSapRefs(emptySAPRefs());
    setSelectedProject('');
    setSelectedSubcontract(null);
    setSubcontractOptions([]);
    setWorkDoneUpTo(today());
    setPaymentNo('PC-1');
    setPaymentType('Interim');
    setInvoiceNo('');
    setInvoiceDate(today());
    // Section 3 is computed from workDoneCum/mosCum (already reset above)
    setWorkDoneCum(0);
    setMaxWorkDoneFromEntries(0);
    setMosCum(0);
    setDeductionCum(0);
    setMaterialBackChargesCum(0);
    setManpowerBackChargesCum(0);
    setEquipmentBackChargesCum(0);
    setOthersCum(0);
    setPrevPC({
      work_done_cum: 0, mos_cum: 0, deduction_cum: 0, retention_cum: 0,
      adv_payment_recovery_cum: 0, performance_bond_cum: 0, advance_guarantee_cum: 0,
      workmens_comp_cum: 0, insurance_cum: 0, tax_clearance_cum: 0,
      material_back_charges_cum: 0, manpower_back_charges_cum: 0,
      equipment_back_charges_cum: 0, others_cum: 0, total_deduction_cum: 0,
      payment_cum: 0, executed_work_pct: 0, executed_work_amt: 0,
      mos_pct: 0, mos_amt: 0,
    });
    setFadFlags([]);
  }, []);

  // ─────────────────────────────────────────────────────────
  // Validate
  // ─────────────────────────────────────────────────────────

  const validate = useCallback((): string[] => {
    const errors: string[] = [];
    if (!selectedProject) errors.push('Project is required');
    if (!selectedSubcontract) errors.push('Subcontract is required');
    if (!workDoneUpTo) errors.push('Work Done Up To date is required');

    // Missing bonds, insurance, and guarantees do not block the certificate.
    // Finance verification controls the deduction lines added during SAP posting.

    // Interim-only validations
    if (!isAdvanced) {
      if (!invoiceNo.trim()) errors.push('Invoice No is required');
      if (!invoiceDate) errors.push('Invoice Date is required');

      // Work Done(A) cumulative cannot exceed total work done from SubcontractWorkDone entries
      if (workDoneCum > maxWorkDoneFromEntries && maxWorkDoneFromEntries > 0) {
        errors.push(`Work Done (A) cumulative (${fmtKWD(workDoneCum)}) cannot exceed total work done (${fmtKWD(maxWorkDoneFromEntries)})`);
      }

      // Back charges cumulative >= previous cumulative (except Others)
      if (materialBackChargesCum < prevPC.material_back_charges_cum) {
        errors.push('Material Back Charges cumulative cannot be less than previous cumulative');
      }
      if (manpowerBackChargesCum < prevPC.manpower_back_charges_cum) {
        errors.push('Manpower Back Charges cumulative cannot be less than previous cumulative');
      }
      if (equipmentBackChargesCum < prevPC.equipment_back_charges_cum) {
        errors.push('Equipment Back Charges cumulative cannot be less than previous cumulative');
      }
    }

    return errors;
  }, [selectedProject, selectedSubcontract, invoiceNo, invoiceDate, workDoneUpTo,
    isAdvanced, workDoneCum, maxWorkDoneFromEntries, materialBackChargesCum, manpowerBackChargesCum,
    equipmentBackChargesCum, prevPC]);

  // ─────────────────────────────────────────────────────────
  // Build payload
  // ─────────────────────────────────────────────────────────

  const buildPayload = useCallback(() => {
    const base: any = {
      subcontract_id: selectedSubcontract?.id,
      project_code: selectedProject,
      payment_no: paymentNo,
      paymenttype: paymentType,
      workdoneupto: workDoneUpTo,
      invoiceno: invoiceNo,
      invoicedate: invoiceDate,
      fad_flags: fadFlags,
    };
    if (isAdvanced) {
      // Advance Payment Certificate: store only the advance value
      base.netpayable = advancePaymentValue;
      base.currentnetpayable = advancePaymentValue;
    } else {
      // Interim: full finance evaluation — use backend field names
      base.cumulativeworkdonepct = executedWorkPct;
      base.cumulativeworkdoneamt = executedWorkAmt;
      base.previouscumulativeworkdoneamt = prevPC.executed_work_amt;
      base.currentworkdoneamt = executedWorkAmt - prevPC.executed_work_amt;
      base.materialsonsitepct = mosPct;
      base.materialsonsiteamt = mosAmt;
      base.previousmaterialsonsiteamt = prevPC.mos_amt;
      base.currentmaterialsonsiteamt = mosAmt - prevPC.mos_amt;
      base.grossvaluation = workDoneCum + mosCum;
      base.retentionpct = retentionPct;
      base.retentionamt = retentionCum;
      base.previousretentionamt = retentionPrev;
      base.currentretentionamt = retentionCum - retentionPrev;
      base.netvaluation = workDoneCum - retentionCum;
      base.previousnetvaluation = prevPC.work_done_cum - prevPC.retention_cum;
      base.currentnetvaluation = (workDoneCum - retentionCum) - (prevPC.work_done_cum - prevPC.retention_cum);
      base.advancepaymentpct = advPaymentPct;
      base.advancerecoverypct = advPaymentPct;
      base.advancerecoveryamt = advPaymentRecoveryCum;
      base.previousadvancerecoveryamt = advPaymentRecoveryPrev;
      base.currentadvancerecoveryamt = advPaymentRecoveryCum - advPaymentRecoveryPrev;
      base.performancebondpct = effectivePerformanceBondPct;
      base.performancebondamt = performanceBondCum;
      base.previousperformancebondamt = performanceBondPrev;
      base.currentperformancebondamt = performanceBondNet;
      base.advanceguaranteepct = effectiveAdvanceGuaranteePct;
      base.advanceguaranteeamt = advanceGuaranteeCum;
      base.previousadvanceguaranteeamt = advanceGuaranteePrev;
      base.currentadvanceguaranteeamt = advanceGuaranteeNet;
      base.workmenscomppct = effectiveWorkmensCompPct;
      base.workmenscompamt = workmensCompCum;
      base.previousworkmenscompamt = workmensCompPrev;
      base.currentworkmenscompamt = workmensCompCum - workmensCompPrev;
      base.insurancepct = effectiveAllRiskInsurancePct;
      base.insuranceamt = insuranceCum;
      base.previousinsuranceamt = insurancePrev;
      base.currentinsuranceamt = insuranceCum - insurancePrev;
      base.taxclearancepct = effectiveTaxClearancePct;
      base.taxclearanceamt = taxClearanceCum;
      base.previoustaxclearanceamt = taxClearancePrev;
      base.currenttaxclearanceamt = taxClearanceCum - taxClearancePrev;
      base.materialbackchargesamt = materialBackChargesCum;
      base.previousmaterialbackchargesamt = prevPC.material_back_charges_cum;
      base.currentmaterialbackchargesamt = materialBackChargesCum - prevPC.material_back_charges_cum;
      base.manpowerbackchargesamt = manpowerBackChargesCum;
      base.previousmanpowerbackchargesamt = prevPC.manpower_back_charges_cum;
      base.currentmanpowerbackchargesamt = manpowerBackChargesCum - prevPC.manpower_back_charges_cum;
      base.equipmentbackchargesamt = equipmentBackChargesCum;
      base.previousequipmentbackchargesamt = prevPC.equipment_back_charges_cum;
      base.currentequipmentbackchargesamt = equipmentBackChargesCum - prevPC.equipment_back_charges_cum;
      base.othersamt = othersCum;
      base.previousothersamt = prevPC.others_cum;
      base.currentothersamt = othersCum - prevPC.others_cum;
      base.deductioncum = deductionCum;
      base.netpayable = paymentCum;
      base.previousnetpayable = paymentPrev;
      base.currentnetpayable = paymentNet;
      base.remarks = '';
    }
    return base;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcontract, selectedProject, paymentNo, paymentType, workDoneUpTo,
    invoiceNo, invoiceDate, isAdvanced, advancePaymentValue, prevPC,
    retentionPct, retentionPrev, workDoneCum, mosCum, retentionCum, advPaymentRecoveryCum,
    effectivePerformanceBondPct, performanceBondCum, performanceBondPrev, performanceBondNet,
    effectiveAdvanceGuaranteePct, advanceGuaranteeCum, advanceGuaranteePrev, advanceGuaranteeNet,
    advPaymentPct, advPaymentRecoveryPrev, workmensCompPct, workmensCompCum, workmensCompPrev,
    allRiskInsurancePct, insuranceCum, insurancePrev, materialBackChargesCum,
    manpowerBackChargesCum, equipmentBackChargesCum, othersCum, deductionCum,
    paymentPrev, paymentCum, paymentNet, fadFlags]);

  // ─────────────────────────────────────────────────────────
  // Save as Draft
  // ─────────────────────────────────────────────────────────

  const handleSaveDraft = useCallback(async () => {
    const errors = validate();
    if (errors.length > 0) {
      dispatch(showSnackbar({ message: errors[0], severity: 'error' }));
      return;
    }
    if (!selectedSubcontract?.id) {
      dispatch(showSnackbar({ message: 'Please select a subcontract first', severity: 'error' }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...buildPayload(), status: 'Draft' };
      const scId = selectedSubcontract.id;
      if (editingPCId) {
        await apiService.put(`/subcontract/${scId}/payment-certificates/${editingPCId}`, payload);
        dispatch(showSnackbar({ message: 'Payment Certificate draft updated', severity: 'success' }));
      } else {
        const res: any = await apiService.post(`/subcontract/${scId}/payment-certificates`, payload);
        const newId = res?.data?.paymentcertificateid || res?.data?.id || res?.id;
        if (newId) setEditingPCId(newId);
        dispatch(showSnackbar({ message: 'Payment Certificate draft saved', severity: 'success' }));
      }
      setPcStatus('Draft');
    } catch (err: any) {
      dispatch(showSnackbar({ message: 'Failed to save draft: ' + (err?.response?.data?.message || err?.message || ''), severity: 'error' }));
    } finally {
      setSubmitting(false);
    }
  }, [validate, buildPayload, editingPCId, selectedSubcontract, dispatch]);

  // ─────────────────────────────────────────────────────────
  // Submit to Finance
  // ─────────────────────────────────────────────────────────

  const handleSubmitToFinance = useCallback(async () => {
    const errors = validate();
    if (errors.length > 0) {
      dispatch(showSnackbar({ message: errors.join('; '), severity: 'error' }));
      return;
    }
    if (!selectedSubcontract?.id) {
      dispatch(showSnackbar({ message: 'Please select a subcontract first', severity: 'error' }));
      return;
    }
    setSubmitting(true);
    setActionStatus({
      open: true,
      status: 'processing',
      title: 'Submitting Payment Certificate',
      message: 'Saving the payment certificate and submitting it to Finance.',
      details: `${paymentNo || 'Payment Certificate'} | ${selectedSubcontract.subcontract_number}`,
    });
    try {
      const payload = { ...buildPayload(), status: 'SubmittedToFinance' };
      const scId = selectedSubcontract.id;
      if (editingPCId) {
        await apiService.put(`/subcontract/${scId}/payment-certificates/${editingPCId}`, payload);
      } else {
        const res: any = await apiService.post(`/subcontract/${scId}/payment-certificates`, payload);
        const newId = res?.data?.paymentcertificateid || res?.data?.id || res?.id;
        if (newId) setEditingPCId(newId);
      }
      setPcStatus('SubmittedToFinance');
      setActionStatus({
        open: true,
        status: 'success',
        title: 'Payment Certificate Submitted',
        message: 'The payment certificate was submitted to Finance successfully.',
        details: `${paymentNo || 'Payment Certificate'} | ${selectedSubcontract.subcontract_number}`,
      });
    } catch (err: any) {
      setActionStatus({
        open: true,
        status: 'error',
        title: 'Payment Certificate Submission Failed',
        message: err?.response?.data?.message || err?.message || 'Failed to submit the payment certificate',
        details: err?.response?.status ? `HTTP status ${err.response.status}` : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }, [validate, buildPayload, editingPCId, selectedSubcontract, paymentNo, dispatch]);

  // ─────────────────────────────────────────────────────────
  // Generate PDF (print-based)
  // ─────────────────────────────────────────────────────────

  const handleGeneratePDF = useCallback(async () => {
    const errors = isReadOnly ? [] : validate();
    if (errors.length > 0) {
      dispatch(showSnackbar({ message: 'Please fix validation errors before generating PDF', severity: 'error' }));
      return;
    }
    if (!selectedSubcontract?.id) {
      dispatch(showSnackbar({ message: 'Please select a subcontract first', severity: 'error' }));
      return;
    }

    if (!isReadOnly) {
      // For editable certificates, save as draft first before generating PDF
      try {
        const payload = { ...buildPayload(), status: 'Draft' };
        const scId = selectedSubcontract.id;
        if (editingPCId) {
          await apiService.put(`/subcontract/${scId}/payment-certificates/${editingPCId}`, payload);
        } else {
          const res: any = await apiService.post(`/subcontract/${scId}/payment-certificates`, payload);
          const newId = res?.data?.paymentcertificateid || res?.data?.id || res?.id;
          if (newId) setEditingPCId(newId);
        }
        setPcStatus('Draft');
        dispatch(showSnackbar({ message: 'Draft saved. Generating PDF...', severity: 'success' }));
      } catch (err: any) {
        dispatch(showSnackbar({ message: 'Failed to save draft before PDF: ' + (err?.response?.data?.message || err?.message || ''), severity: 'error' }));
        return;
      }
    }

    const printContent = printRef.current;
    if (!printContent) return;

    // Build HTML sections using array concatenation to avoid nested template literal issues.
    // The composed HTML is then POSTed to the backend's /pdf/render endpoint, which
    // renders it via Puppeteer and returns a real PDF buffer. This replaces the legacy
    // `window.open('','_blank') + printWindow.print()` flow, which was unreliable on
    // mobile browsers (popup blockers) and inconsistent across browser families.
    const pdfSections: string[] = [];

    // CSS — matches the expected "SO Payment Certificate" template
    const css = [
      '@page { size: A4 portrait; margin: 8mm 10mm; }',
      '* { box-sizing: border-box; margin: 0; padding: 0; }',
      'html, body { width: 100%; height: 100%; overflow: hidden; }',
      'body { font-family: Arial, Helvetica, sans-serif; font-size: 8.5px; color: #000; padding: 2px; }',
      'table { width: 100%; border-collapse: collapse; }',
      'th, td { border: 1px solid #000; padding: 2px 4px; font-size: 8px; line-height: 1.25; vertical-align: middle; }',
      'th { font-weight: bold; text-align: center; }',
      'td.r { text-align: right; }',
      'td.c { text-align: center; }',
      'td.nb, th.nb, tr.nb td { border: none; }',
      'td.bt0 { border-top: none; }',
      'td.bb0 { border-bottom: none; }',
      'td.bl0 { border-left: none; }',
      'td.br0 { border-right: none; }',
      '.bold, b { font-weight: bold; }',
      '.title-area { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }',
      '.title-area .logo { font-size: 16px; font-weight: bold; }',
      '.title-area .logo-sub { font-size: 7px; letter-spacing: 1px; }',
      '.title-area .center-title { font-size: 14px; font-weight: bold; text-align: center; flex: 1; }',
      '.title-area .pc-info { text-align: right; font-size: 8.5px; }',
      '.section-title { font-weight: bold; font-size: 9px; margin: 5px 0 2px; }',
      '.sig-line { border-top: 1px solid #000; width: 120px; display: inline-block; margin-top: 18px; }',
      '.sig-row { display: flex; justify-content: space-between; margin-top: 8px; }',
      '.sig-block { text-align: center; font-size: 8px; width: 30%; }',
      '.footer-row { display: flex; justify-content: space-between; font-size: 7px; margin-top: 6px; border-top: 1px solid #000; padding-top: 2px; }',
      'tr.total-row td { font-weight: bold; }',
      'tr.payment-row td { font-weight: bold; }',
      '.chk-grid { margin: 3px 0; }',
      '.chk-grid td { border: none; font-size: 8px; padding: 1px 4px; }',
      '.note { font-size: 7.5px; margin: 2px 0; }',
      '.amount-words { font-style: italic; font-size: 8px; padding: 2px 4px; }',
    ].join('\n');

    // Helper: format date for display
    const fmtDate = (d: string | null | undefined) => {
      if (!d) return 'N/A';
      try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
    };

    // ── Title Area (Logo + Title + PC Info) ──
    pdfSections.push([
      '<div class="title-area">',
      '<div><div class="logo">Dhow</div><div class="logo-sub">ENGINEERING</div></div>',
      '<div class="center-title">PAYMENT CERTIFICATE</div>',
      '<div class="pc-info">Payment Certificate No.: <b>' + paymentNo + '</b><br/>Date: <b>' + fmtDate(invoiceDate) + '</b></div>',
      '</div>',
    ].join('\n'));

    // ── Header Info Table (3-column bordered table matching expected format) ──
    const completionDate = sc?.completion_date || '';
    const amendedCompletion = sc?.amended_completion_date || 'N/A';
    pdfSections.push([
      '<table style="margin-bottom:4px">',
      '<tr>',
      '  <td style="width:17%"><b>Division:</b></td>',
      '  <td style="width:16%">' + (sc?.division || '') + '</td>',
      '  <td style="width:17%"><b>Work done up to:</b></td>',
      '  <td style="width:16%">' + fmtDate(workDoneUpTo) + '</td>',
      '  <td style="width:17%"><b>Payment Type:</b></td>',
      '  <td style="width:17%">' + paymentType + '</td>',
      '</tr>',
      '<tr>',
      '  <td><b>Project Code:</b></td>',
      '  <td>' + selectedProject + '</td>',
      '  <td><b>Payment No.:</b></td>',
      '  <td>' + paymentNo + '</td>',
      '  <td><b>Invoice No:</b></td>',
      '  <td>' + invoiceNo + '</td>',
      '</tr>',
      '<tr>',
      '  <td><b>Subcontract No:</b></td>',
      '  <td>' + (sc?.subcontract_number || '') + '</td>',
      '  <td><b>Contract start date:</b></td>',
      '  <td>' + fmtDate(sc?.start_date) + '</td>',
      '  <td><b>Invoice Date:</b></td>',
      '  <td>' + fmtDate(invoiceDate) + '</td>',
      '</tr>',
      '<tr>',
      '  <td><b>Project Name:</b></td>',
      '  <td>' + (sc?.subcontractor_name || '') + '</td>',
      '  <td><b>Contract Type:</b> "Re-measured"</td>',
      '  <td><b>Original Value:</b> ' + fmtKWD(originalValue) + '/-</td>',
      '  <td><b>Completion Date:</b></td>',
      '  <td>' + fmtDate(completionDate) + '</td>',
      '</tr>',
      '<tr>',
      '  <td colspan="2"></td>',
      '  <td><b>Approved Variations:</b> ' + (approvedVariations ? fmtKWD(approvedVariations) : 'N/A') + '</td>',
      '  <td><b>Total:</b> ' + fmtKWD(totalContractValue) + '/-</td>',
      '  <td><b>Amended Completion Date:</b></td>',
      '  <td>' + (amendedCompletion !== 'N/A' ? fmtDate(amendedCompletion) : 'N/A') + '</td>',
      '</tr>',
      '</table>',
    ].join('\n'));

    // ── Work Done Section ──
    if (!isAdvanced) {
      const r = (label: string, cumPct: number, cumAmt: number, prevPct: number, prevAmt: number) => {
        const netPct = cumPct - prevPct;
        const netAmt = cumAmt - prevAmt;
        return '<tr><td style="width:16%">' + label + '</td>'
          + '<td class="c" style="width:9%">' + fmtPct(cumPct) + '</td><td class="r" style="width:14%">' + fmtKWD(cumAmt) + '</td>'
          + '<td class="c" style="width:9%">' + fmtPct(prevPct) + '</td><td class="r" style="width:14%">' + fmtKWD(prevAmt) + '</td>'
          + '<td class="c" style="width:9%">' + fmtPct(netPct) + '</td><td class="r" style="width:14%">' + fmtKWD(netAmt) + '</td></tr>';
      };
      const totCumPct = executedWorkPct + mosPct;
      const totCumAmt = executedWorkAmt + mosAmt;
      const totPrevPct = prevPC.executed_work_pct + prevPC.mos_pct;
      const totPrevAmt = prevPC.executed_work_amt + prevPC.mos_amt;
      const netTotalAmt = totCumAmt - totPrevAmt;
      const totInWords = kwdToWords(netTotalAmt);
      pdfSections.push([
        '<div class="section-title">Work Done</div>',
        '<table>',
        '<thead>',
        '<tr><th rowspan="2" style="width:16%">Activities/ Task</th><th colspan="2">Cumulative Certified till Date</th><th colspan="2">Cumulative Certified Previous</th><th colspan="2">Net to be Paid</th></tr>',
        '<tr><th>(%)</th><th>Amount</th><th>(%)</th><th>Amount</th><th>(%)</th><th>Amount</th></tr>',
        '</thead><tbody>',
        r('Executed Work', executedWorkPct, executedWorkAmt, prevPC.executed_work_pct, prevPC.executed_work_amt),
        r('Material On-Site', mosPct, mosAmt, prevPC.mos_pct, prevPC.mos_amt),
        '<tr class="total-row"><td><b>Total</b></td>'
          + '<td class="c"><b>' + fmtPct(totCumPct) + '</b></td><td class="r"><b>' + fmtKWD(totCumAmt) + '</b></td>'
          + '<td class="c"><b>' + fmtPct(totPrevPct) + '</b></td><td class="r"><b>' + fmtKWD(totPrevAmt) + '</b></td>'
          + '<td class="c"><b>' + fmtPct(totCumPct - totPrevPct) + '</b></td><td class="r"><b>' + fmtKWD(netTotalAmt) + '</b></td></tr>',
        '<tr><td>Total in Words</td><td colspan="6" style="font-style:italic">' + totInWords + '</td></tr>',
        '<tr><td>Deductions to Apply:</td><td colspan="6">(Other Than Material, Equipment &amp; Manpower)</td></tr>',
        '</tbody></table>',
      ].join('\n'));
    }

    // ── Signature Row 1 (Quantity Surveyor, Project Manager, PCSD) ──
    pdfSections.push([
      '<div class="sig-row">',
      '<div class="sig-block">Quantity Surveyor:<br/><span class="sig-line"></span></div>',
      '<div class="sig-block">Project Manager:<br/><span class="sig-line"></span></div>',
      '<div class="sig-block">PCSD:<br/><span class="sig-line"></span></div>',
      '</div>',
      '<div class="sig-row">',
      '<div class="sig-block">Manager of Projects:<br/><span class="sig-line"></span></div>',
      '<div class="sig-block">Operations Director:<br/><span class="sig-line"></span></div>',
      '<div class="sig-block">Managing Director:<br/><span class="sig-line"></span></div>',
      '</div>',
    ].join('\n'));

    // ── FAD's Review ──
    const isDocReceived = (key: string) => fadFlags.find((f) => f.key === key)?.received || false;
    const chk = (received: boolean) => received ? '&#9745;' : '&#9744;';
    pdfSections.push([
      '<div style="margin-top:6px">',
      '<p style="font-size:8.5px;margin-bottom:3px"><b>FAD\'s Review:</b> Following documents are required to process the payment:</p>',
      '<table class="chk-grid"><tbody>',
      '<tr>'
        + '<td>' + chk(isDocReceived('LetterOfAuthorization')) + ' Letter of authorization</td>'
        + '<td>' + chk(isDocReceived('PerformanceBond')) + ' Performance Bond</td>'
        + '<td>' + chk(isDocReceived('AllRiskInsurance')) + ' All Risk Insurance</td>'
        + '</tr>',
      '<tr>'
        + '<td>' + chk(isDocReceived('TaxClearanceCert')) + ' Tax clearance certificate</td>'
        + '<td>' + chk(isDocReceived('AdvancePaymentGuarantee')) + ' Advance Payment guarantee</td>'
        + '<td>' + chk(isDocReceived('WorkmensCompInsurance')) + ' Workmen\'s compensation insurance</td>'
        + '</tr>',
      '<tr>'
        + '<td>' + chk(isDocReceived('RetentionGuarantee')) + ' Retention guarantee</td>'
        + '<td>' + chk(false) + ' Other _______________</td>'
        + '<td></td>'
        + '</tr>',
      '</tbody></table>',
      '<p class="note">Note: Copy to be sent to PM for action</p>',
      '</div>',
    ].join('\n'));

    // ── Finance and Accounting signature line ──
    pdfSections.push([
      '<div style="display:flex;justify-content:flex-end;margin:4px 0;gap:30px;font-size:8px">',
      '<div><span class="sig-line"></span><br/>Finance and Accounting</div>',
      '<div><span class="sig-line"></span><br/>Date</div>',
      '</div>',
    ].join('\n'));

    // ── Finance and Accounting Division's Evaluation ──
    if (isAdvanced) {
      pdfSections.push([
        '<div class="section-title">Finance and Accounting Division\'s Evaluation:</div>',
        '<table><tbody>',
        '<tr><td>Contract Amount (KWD)</td><td class="r">' + fmtKWD(totalContractValue) + '</td><td></td><td></td><td></td></tr>',
        '<tr><td>Advance Payment %</td><td class="r">' + fmtPct(advPaymentPct) + '</td><td></td><td></td><td></td></tr>',
        '<tr class="payment-row"><td>Advance Payment Value (KWD)</td><td class="r">' + fmtKWD(advancePaymentValue) + '</td><td></td><td></td><td></td></tr>',
        '</tbody></table>',
        '<div class="amount-words"><b>In words (cumulative):</b> KWD ' + paymentWords + '</div>',
      ].join('\n'));
    } else {
      // Interim - Finance & Accounting Evaluation
      const fr = (desc: string, cum: string, prev: string, net: string, bold?: boolean) => {
        const w = bold ? '<b>' : '';
        const wc = bold ? '</b>' : '';
        return '<tr' + (bold ? ' class="total-row"' : '') + '><td>' + w + desc + wc + '</td><td class="r">' + w + cum + wc + '</td><td class="r">' + w + prev + wc + '</td><td class="r">' + w + net + wc + '</td><td></td></tr>';
      };

      pdfSections.push([
        '<div class="section-title" style="font-style:italic;text-decoration:underline">Finance and Accounting Division\'s Evaluation:</div>',
        '<table><thead><tr><th style="width:28%"></th><th style="width:18%">Cumulative</th><th style="width:18%">Previous</th><th style="width:18%">Net</th><th style="width:18%">Remarks</th></tr></thead>',
        '<tbody>',
        fr('Work Done (A)', fmtKWD(workDoneCum), fmtKWD(prevPC.work_done_cum), fmtKWD(workDoneNet)),
        fr('MOS (Material on Site)', fmtKWD(mosCum), fmtKWD(prevPC.mos_cum), fmtKWD(mosNet)),
        fr('Deductions', fmtKWD(deductionCum), fmtKWD(prevPC.deduction_cum), fmtKWD(deductionNet)),
        fr('Retentions', fmtKWD(retentionCum), fmtKWD(retentionPrev), fmtKWD(retentionNet)),
        fr('Performance Bond LG', '0.000', '0.000', '0.000'),
        fr('Advance Payment Recovery', fmtKWD(advPaymentRecoveryCum), fmtKWD(advPaymentRecoveryPrev), fmtKWD(advPaymentRecoveryNet)),
        fr("Workmen's Compensation", fmtKWD(workmensCompCum), fmtKWD(workmensCompPrev), fmtKWD(workmensCompNet)),
        fr('Insurances', fmtKWD(insuranceCum), fmtKWD(insurancePrev), fmtKWD(insuranceNet)),
        fr('Material back charges', fmtKWD(materialBackChargesCum), fmtKWD(prevPC.material_back_charges_cum), fmtKWD(materialBackChargesNet)),
        fr('Manpower back charges', fmtKWD(manpowerBackChargesCum), fmtKWD(prevPC.manpower_back_charges_cum), fmtKWD(manpowerBackChargesNet)),
        fr('Equipment back charges', fmtKWD(equipmentBackChargesCum), fmtKWD(prevPC.equipment_back_charges_cum), fmtKWD(equipmentBackChargesNet)),
        fr('Others', fmtKWD(othersCum), fmtKWD(prevPC.others_cum), fmtKWD(othersNet)),
        fr('Total Deduction (B)', fmtKWD(totalDeductionCum), fmtKWD(totalDeductionPrev), fmtKWD(totalDeductionNet), true),
        '<tr class="payment-row"><td><b>Payment (C=A-B)</b></td><td class="r"><b>' + fmtKWD(paymentCum) + '</b></td><td class="r"><b>' + fmtKWD(paymentPrev) + '</b></td><td class="r' + (paymentNet < 0 ? '" style="color:#d32f2f' : '') + '"><b>' + fmtKWD(paymentNet) + '</b></td><td></td></tr>',
        '<tr><td colspan="2">In words (cumulative): <b>KWD</b> ' + kwdToWords(paymentCum) + '</td><td colspan="3"></td></tr>',
        '</tbody></table>',
      ].join('\n'));
    }

    // ── Finance and Accounting Department + Auditor signatures ──
    pdfSections.push([
      '<div style="display:flex;justify-content:space-between;margin-top:8px;font-size:8px">',
      '<div><span class="sig-line"></span><br/>Finance and Accounting Department</div>',
      '<div><span class="sig-line"></span><br/>Date</div>',
      '<div><span class="sig-line"></span><br/>Auditor</div>',
      '<div><span class="sig-line"></span><br/>Date</div>',
      '</div>',
    ].join('\n'));

    // ── Acknowledgement ──
    pdfSections.push([
      '<div style="margin-top:6px;font-size:8px">',
      '<p><b>Acknowledgement:</b> The above evaluation is accepted</p>',
      '<div style="display:flex;justify-content:center;gap:60px;margin-top:10px">',
      '<div>Name <span class="sig-line"></span></div>',
      '<div>Signature <span class="sig-line"></span></div>',
      '<div>Date <span class="sig-line"></span></div>',
      '</div>',
      '</div>',
    ].join('\n'));

    // ── Distribution note ──
    pdfSections.push([
      '<p class="note" style="margin-top:4px"><b>Distribution:</b> Original &ndash; Finance and Accounting Department/ <b>Note:</b> Foreign companies will not be required to sign Payment Certificates to acknowledge evaluation of interim payments. However, they will still be required to sign on the original Final Payment Certificate.</p>',
    ].join('\n'));

    // ── Footer ──
    pdfSections.push([
      '<div class="footer-row">',
      '<span>Issue 4.0/ ' + fmtDate(invoiceDate) + '</span>',
      '<span>Page 1 of 1</span>',
      '<span>MSP 250/ Form 11</span>',
      '</div>',
    ].join('\n'));

    // Auto-scale script: measures content height vs A4 portrait page height and shrinks if needed
    const scaleScript = [
      '<script>',
      'window.addEventListener("load", function() {',
      '  var content = document.getElementById("pc-content");',
      '  // A4 portrait usable height: 297mm - 16mm margins = 281mm ~ 1062px at 96dpi',
      '  var pageH = 1062;',
      '  var contentH = content.scrollHeight;',
      '  if (contentH > pageH) {',
      '    var scale = pageH / contentH;',
      '    // Clamp to minimum 0.55 to keep readable',
      '    if (scale < 0.55) scale = 0.55;',
      '    content.style.transform = "scale(" + scale + ")";',
      '    content.style.transformOrigin = "top left";',
      '    content.style.width = (100 / scale) + "%";',
      '  }',
      '});',
      '</script>',
    ].join('\n');

    const composedHtml =
      '<!DOCTYPE html><html><head><title>Payment Certificate - ' + paymentNo + '</title><style>' + css + '</style></head><body>'
      + '<div id="pc-content">'
      + pdfSections.join('\n')
      + '</div>'
      + scaleScript
      + '</body></html>';

    try {
      const filename = `payment-certificate-${paymentNo || 'PC'}.pdf`;
      const blob = await apiService.renderPdf(composedHtml, { filename, format: 'A4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so iOS Safari has time to actually start the download.
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, 4000);
      dispatch(showSnackbar({ message: 'Payment Certificate PDF downloaded', severity: 'success' }));
    } catch (err: any) {
      dispatch(showSnackbar({
        message: 'Failed to generate PDF: ' + (err?.response?.data?.message || err?.message || ''),
        severity: 'error',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, validate, dispatch, buildPayload, editingPCId, selectedSubcontract,
    paymentNo, selectedProject, sc, paymentType, workDoneUpTo,
    invoiceNo, invoiceDate, originalValue, approvedVariations, totalContractValue,
    prevPC, fadFlags, isAdvanced, advancePaymentValue,
    executedWorkAmt, executedWorkPct, mosAmt, mosPct,
    workDoneCum, mosCum, deductionCum, retentionCum, retentionPct,
    advPaymentRecoveryCum, advPaymentPct, workmensCompCum, workmensCompPct,
    insuranceCum, allRiskInsurancePct, materialBackChargesCum, manpowerBackChargesCum,
    equipmentBackChargesCum, othersCum, totalDeductionCum, paymentCum,
    workDoneNet, mosNet, deductionNet, retentionNet, retentionPrev,
    advPaymentRecoveryNet, advPaymentRecoveryPrev, workmensCompNet, workmensCompPrev,
    insuranceNet, insurancePrev, materialBackChargesNet, manpowerBackChargesNet,
    equipmentBackChargesNet, othersNet, totalDeductionNet, totalDeductionPrev,
    paymentNet, paymentPrev, paymentWords]);

  // ─────────────────────────────────────────────────────────
  // Filtered PC list
  // ─────────────────────────────────────────────────────────

  const filteredPCList = useMemo(() => {
    let list = pcList;
    if (pcListFilter !== 'All') {
      list = list.filter((pc) => pc.status === pcListFilter);
    }
    if (pcSearchQuery.trim()) {
      const q = pcSearchQuery.toLowerCase();
      list = list.filter((pc) =>
        pc.pc_number.toLowerCase().includes(q) ||
        pc.subcontract_number.toLowerCase().includes(q) ||
        pc.project_code.toLowerCase().includes(q)
      );
    }
    return list;
  }, [pcList, pcListFilter, pcSearchQuery]);

  // ─────────────────────────────────────────────────────────
  // Render: Section header
  // ─────────────────────────────────────────────────────────

  const SectionHeader: React.FC<{ title: string; number?: string }> = ({ title, number }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1, color: '#1565c0', borderBottom: '2px solid #1565c0', pb: 0.5 }}>
      {number ? `Section ${number} - ` : ''}{title}
    </Typography>
  );

  // ─────────────────────────────────────────────────────────
  // Render: Header field (read only)
  // ─────────────────────────────────────────────────────────

  const ReadOnlyField: React.FC<{ label: string; value: string | number; xs?: number }> = ({ label, value, xs = 3 }) => (
    <Grid item xs={12} sm={xs}>
      <TextField
        label={label}
        value={value}
        size="small"
        fullWidth
        InputProps={{ readOnly: true }}
        sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }}
      />
    </Grid>
  );

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────

  return (
    <Box
      component="section"
      aria-labelledby="pc-heading"
      sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: 'auto' }}
    >
      <Typography
        component="h1"
        id="pc-heading"
        variant="h5"
        sx={{ fontWeight: 700, mb: 1, fontSize: { xs: 18, sm: 22, md: 24 } }}
      >
        Payment Certificate
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        MSP 250 / Form 11 &mdash; Dhow Engineering Payment Certificate
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab label="Create / Edit" />
        <Tab label="Payment Certificate List" />
      </Tabs>

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 0: CREATE / EDIT FORM                          */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 0 && (
        <Box ref={printRef}>
          {/* Loading overlay */}
          {(loading || submitting) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <>
              {/* Status chip + action bar */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                {editingPCId && (
                  <Chip
                    label={pcStatus}
                    color={pcStatus === 'Draft' ? 'warning' : pcStatus === 'SubmittedToFinance' ? 'info' : 'success'}
                    size="small"
                  />
                )}
                {editingPCId && <Typography variant="body2" color="text.secondary">PC ID: {editingPCId}</Typography>}
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={resetForm}>
                  New PC
                </Button>
              </Stack>

              {/* Negative payment warning */}
              {paymentNet < 0 && selectedSubcontract && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Payment (C = A - B) is negative: <strong>{fmtKWD(paymentNet)} KWD</strong>. This means deductions exceed the work done amount.
                </Alert>
              )}

              {isReadOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This Payment Certificate has been submitted and is read-only.
                </Alert>
              )}

              {(sapRefs.grpoDocNum || sapRefs.grpoDocEntry || sapRefs.invoiceDocNum || sapRefs.invoiceDocEntry || sapRefs.goodsIssueDocNum || sapRefs.goodsIssueDocEntry) && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafcff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    SAP References
                  </Typography>
                  <Grid container spacing={2}>
                    <ReadOnlyField
                      label="Linked GRPO"
                      value={
                        sapRefs.grpoDocNum || sapRefs.grpoDocEntry
                          ? `DocNum ${sapRefs.grpoDocNum || '-'} / DocEntry ${sapRefs.grpoDocEntry || '-'}`
                          : '-'
                      }
                      xs={4}
                    />
                    <ReadOnlyField
                      label={paymentType === 'Advanced' ? 'SAP AP Down Payment' : 'SAP AP Invoice'}
                      value={
                        sapRefs.invoiceDocNum || sapRefs.invoiceDocEntry
                          ? `DocNum ${sapRefs.invoiceDocNum || '-'} / DocEntry ${sapRefs.invoiceDocEntry || '-'}`
                          : 'Not posted yet'
                      }
                      xs={4}
                    />
                    <ReadOnlyField
                      label="SAP Goods Issue"
                      value={
                        sapRefs.goodsIssueDocNum || sapRefs.goodsIssueDocEntry
                          ? `DocNum ${sapRefs.goodsIssueDocNum || '-'} / DocEntry ${sapRefs.goodsIssueDocEntry || '-'}`
                          : (paymentType === 'Advanced' ? 'Not applicable' : 'Not posted yet')
                      }
                      xs={4}
                    />
                  </Grid>
                </Paper>
              )}

              {/* ─── Project & Subcontract Selection ─── */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Selection</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Project Number"
                      value={selectedProject}
                      size="small"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setProjectPickerOpen(true)} disabled={isReadOnly}>
                              <SearchIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => !isReadOnly && setProjectPickerOpen(true)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl size="small" fullWidth disabled={!selectedProject || isReadOnly}>
                      <InputLabel>Subcontract Number</InputLabel>
                      <Select
                        value={selectedSubcontract?.id?.toString() || ''}
                        label="Subcontract Number"
                        onChange={(e) => {
                          const sc = subcontractOptions.find((s) => s.id.toString() === e.target.value);
                          if (sc) loadSubcontractData(sc);
                        }}
                      >
                        {subcontractOptions.map((s) => (
                          <MenuItem key={s.id} value={s.id.toString()}>
                            {s.subcontract_number}
                            {s.contract_number && String(s.contract_number).trim() !== String(s.subcontract_number || '').trim() ? ` (${s.contract_number})` : ''}
                            {' - '}
                            {s.subcontractor_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Subcontractor" value={sc?.subcontractor_name || ''} size="small" fullWidth InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }} />
                  </Grid>
                </Grid>
              </Paper>

              {/* ─── Header Fields ─── */}
              {selectedSubcontract && (
                <>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Certificate Details</Typography>
                    <Grid container spacing={2}>
                      <ReadOnlyField label="Contract Type" value="Re-measured" />
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Work Done Up To"
                          type="date"
                          value={workDoneUpTo}
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => { setWorkDoneUpTo(e.target.value); if (!invoiceDate || invoiceDate === workDoneUpTo) setInvoiceDate(e.target.value); }}
                          disabled={isReadOnly}
                        />
                      </Grid>
                      <ReadOnlyField label="Payment No" value={paymentNo} />
                      <ReadOnlyField label="Payment Type" value={paymentType} />

                      <ReadOnlyField label="Contract Start Date" value={sc?.start_date || ''} />
                      <ReadOnlyField label="Original Value (KWD)" value={fmtKWD(originalValue)} />
                      <ReadOnlyField label="Approved Variations (KWD)" value={approvedVariations ? fmtKWD(approvedVariations) : 'N/A'} />
                      <ReadOnlyField label="Total (KWD)" value={fmtKWD(totalContractValue)} />

                      <Grid item xs={12} sm={3}>
                        <TextField label="Invoice No" value={invoiceNo} size="small" fullWidth onChange={(e) => setInvoiceNo(e.target.value)} disabled={isReadOnly} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField label="Invoice Date" type="date" value={invoiceDate} size="small" fullWidth InputLabelProps={{ shrink: true }} onChange={(e) => setInvoiceDate(e.target.value)} disabled={isReadOnly} />
                      </Grid>
                      <ReadOnlyField label="Completion Date" value={sc?.completion_date || ''} />
                      <ReadOnlyField label="Amended Completion" value={sc?.amended_completion_date || 'N/A'} />

                      <ReadOnlyField label="Retention %" value={fmtPct(retentionPct)} />
                      <ReadOnlyField label="Advance Payment %" value={fmtPct(advPaymentPct)} />
                      <ReadOnlyField label="Workmen's Comp %" value={fmtPct(workmensCompPct)} />
                      <ReadOnlyField label="All Risk Insurance %" value={fmtPct(allRiskInsurancePct)} />
                    </Grid>
                  </Paper>

                  {/* ─── Section 3: Work Done (Interim only) ─── */}
                  {!isAdvanced && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <SectionHeader title="Work Done" number="3" />
                    <Table size="small" sx={{ '& th, & td': cellSx }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headCellSx} />
                          <TableCell sx={headCellSx} colSpan={2}>Cumulative</TableCell>
                          <TableCell sx={headCellSx} colSpan={2}>Previous</TableCell>
                          <TableCell sx={headCellSx} colSpan={2}>Net</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={headCellSx} />
                          <TableCell sx={headCellSx}>%</TableCell>
                          <TableCell sx={headCellSx}>Amount (KWD)</TableCell>
                          <TableCell sx={headCellSx}>%</TableCell>
                          <TableCell sx={headCellSx}>Amount (KWD)</TableCell>
                          <TableCell sx={headCellSx}>%</TableCell>
                          <TableCell sx={headCellSx}>Amount (KWD)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={cellSx}>Executed Work</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(executedWorkPct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(executedWorkAmt)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(prevPC.executed_work_pct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.executed_work_amt)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(executedWorkPct - prevPC.executed_work_pct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(executedWorkAmt - prevPC.executed_work_amt)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={cellSx}>Material On Site</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(mosPct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(mosAmt)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(prevPC.mos_pct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.mos_amt)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(mosPct - prevPC.mos_pct)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(mosAmt - prevPC.mos_amt)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                          <TableCell sx={cellSx}><strong>Total</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtPct(executedWorkPct + mosPct)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD(executedWorkAmt + mosAmt)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtPct(prevPC.executed_work_pct + prevPC.mos_pct)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD(prevPC.executed_work_amt + prevPC.mos_amt)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtPct((executedWorkPct + mosPct) - (prevPC.executed_work_pct + prevPC.mos_pct))}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD((executedWorkAmt + mosAmt) - (prevPC.executed_work_amt + prevPC.mos_amt))}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                  )}

                  {/* ─── Section 4: FAD Review ─── */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <SectionHeader title="FAD Review" number="4" />
                    {(() => {
                      // Section 4 FAD items — 7 specific documents, flagged by received status from SubcontractDocuments
                      const fadItems: { label: string; key: string }[] = [
                        { label: 'Letter of Authorization', key: 'LetterOfAuthorization' },
                        { label: 'Performance Bond', key: 'PerformanceBond' },
                        { label: 'All Risk Insurance', key: 'AllRiskInsurance' },
                        { label: 'Tax Clearance Certificate', key: 'TaxClearanceCert' },
                        { label: 'Advance Payment Guarantee', key: 'AdvancePaymentGuarantee' },
                        { label: "Workmen's Compensation Insurance", key: 'WorkmensCompInsurance' },
                        { label: 'Retention Guarantee', key: 'RetentionGuarantee' },
                      ];
                      const isChecked = (key: string) => {
                        const flag = fadFlags.find(f => f.key === key);
                        return flag ? flag.received : false;
                      };
                      return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1 }}>
                          {fadItems.map((item) => (
                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Checkbox checked={isChecked(item.key)} size="small" disabled sx={{ p: 0.3 }} />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{item.label}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Paper>

                  {/* ─── Advance Payment (Advanced type only) ─── */}
                  {isAdvanced && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <SectionHeader title="Advance Payment" number="5" />
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ ...cellSx, width: '40%' }}>Contract Amount (KWD)</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(totalContractValue)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={cellSx}>Advance Payment %</TableCell>
                          <TableCell sx={computedCellSx}>{fmtPct(advPaymentPct)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { fontWeight: 700, backgroundColor: '#e8f5e9' } }}>
                          <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Advance Payment Value (KWD)</TableCell>
                          <TableCell sx={{ ...computedCellSx, backgroundColor: '#e8f5e9' }}>
                            <strong>{fmtKWD(advancePaymentValue)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    {/* Amount in words */}
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fafafa', border: '1px solid #ddd', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        <strong>Advance Payment Amount in Words:</strong> {paymentWords}
                      </Typography>
                    </Box>
                  </Paper>
                  )}

                  {/* ─── Section 5: Finance & Accounting Evaluation (Interim only) ─── */}
                  {!isAdvanced && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <SectionHeader title="Finance & Accounting Evaluation" number="5" />
                    <Table size="small" sx={{ '& th, & td': cellSx }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...headCellSx, width: '30%' }}>Description</TableCell>
                          <TableCell sx={{ ...headCellSx, width: '23%' }}>Cumulative (KWD)</TableCell>
                          <TableCell sx={{ ...headCellSx, width: '23%' }}>Previous (KWD)</TableCell>
                          <TableCell sx={{ ...headCellSx, width: '24%' }}>Net (KWD)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Work Done (A) */}
                        <TableRow>
                          <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Work Done (A)</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput
                              value={workDoneCum}
                              onChange={(v) => setWorkDoneCum(maxWorkDoneFromEntries > 0 ? Math.min(v, maxWorkDoneFromEntries) : v)}
                              disabled={isReadOnly}
                              max={maxWorkDoneFromEntries > 0 ? maxWorkDoneFromEntries : undefined}
                            />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.work_done_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(workDoneNet)}</TableCell>
                        </TableRow>

                        {/* MOS */}
                        <TableRow>
                          <TableCell sx={cellSx}>MOS</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput value={mosCum} onChange={setMosCum} disabled={isReadOnly} />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.mos_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(mosNet)}</TableCell>
                        </TableRow>

                        {/* Deduction */}
                        <TableRow>
                          <TableCell sx={cellSx}>Deduction</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput value={deductionCum} onChange={setDeductionCum} disabled={isReadOnly} />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.deduction_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(deductionNet)}</TableCell>
                        </TableRow>

                        {/* Retention (computed) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Retention ({fmtPct(retentionPct)})</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(retentionCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(retentionPrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(retentionNet)}</TableCell>
                        </TableRow>

                        {/* Adv Payment Recovery (computed) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Adv Payment Recovery ({fmtPct(advPaymentPct)})</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advPaymentRecoveryCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advPaymentRecoveryPrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advPaymentRecoveryNet)}</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={cellSx}>
                            Performance Bond ({fmtPct(effectivePerformanceBondPct)})
                            {performanceBondPct > 0 && effectivePerformanceBondPct === 0 && (
                              <Chip label="Received" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(performanceBondCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(performanceBondPrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(performanceBondNet)}</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={cellSx}>
                            Advance Payment Guarantee ({fmtPct(effectiveAdvanceGuaranteePct)})
                            {advPaymentPct > 0 && effectiveAdvanceGuaranteePct === 0 && (
                              <Chip label="Received" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advanceGuaranteeCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advanceGuaranteePrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(advanceGuaranteeNet)}</TableCell>
                        </TableRow>

                        {/* Workmen's Comp (computed — only deducted if required & not received) */}
                        <TableRow>
                          <TableCell sx={cellSx}>
                            Workmen's Comp ({fmtPct(effectiveWorkmensCompPct)})
                            {workmensCompPct > 0 && effectiveWorkmensCompPct === 0 && (
                              <Chip label="Received" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(workmensCompCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(workmensCompPrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(workmensCompNet)}</TableCell>
                        </TableRow>

                        {/* Insurance (computed — only deducted if required & not received) */}
                        <TableRow>
                          <TableCell sx={cellSx}>
                            Insurance ({fmtPct(effectiveAllRiskInsurancePct)})
                            {allRiskInsurancePct > 0 && effectiveAllRiskInsurancePct === 0 && (
                              <Chip label="Received" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(insuranceCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(insurancePrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(insuranceNet)}</TableCell>
                        </TableRow>

                        {/* Tax Clearance (computed — only deducted if required & not received) */}
                        <TableRow>
                          <TableCell sx={cellSx}>
                            Tax Clearance ({fmtPct(effectiveTaxClearancePct)})
                            {taxClearancePct > 0 && effectiveTaxClearancePct === 0 && (
                              <Chip label="Received" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(taxClearanceCum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(taxClearancePrev)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(taxClearanceNet)}</TableCell>
                        </TableRow>

                        {/* Material Back Charges (manual, >= prev) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Material Back Charges</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput
                              value={materialBackChargesCum}
                              onChange={setMaterialBackChargesCum}
                              disabled={isReadOnly}
                              min={prevPC.material_back_charges_cum}
                            />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.material_back_charges_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(materialBackChargesNet)}</TableCell>
                        </TableRow>

                        {/* Manpower Back Charges (manual, >= prev) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Manpower Back Charges</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput
                              value={manpowerBackChargesCum}
                              onChange={setManpowerBackChargesCum}
                              disabled={isReadOnly}
                              min={prevPC.manpower_back_charges_cum}
                            />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.manpower_back_charges_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(manpowerBackChargesNet)}</TableCell>
                        </TableRow>

                        {/* Equipment Back Charges (manual, >= prev) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Equipment Back Charges</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput
                              value={equipmentBackChargesCum}
                              onChange={setEquipmentBackChargesCum}
                              disabled={isReadOnly}
                              min={prevPC.equipment_back_charges_cum}
                            />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.equipment_back_charges_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(equipmentBackChargesNet)}</TableCell>
                        </TableRow>

                        {/* Others (manual, can be lower) */}
                        <TableRow>
                          <TableCell sx={cellSx}>Others</TableCell>
                          <TableCell sx={inputCellSx}>
                            <CellInput value={othersCum} onChange={setOthersCum} disabled={isReadOnly} />
                          </TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(prevPC.others_cum)}</TableCell>
                          <TableCell sx={computedCellSx}>{fmtKWD(othersNet)}</TableCell>
                        </TableRow>

                        {/* Total Deduction (B) */}
                        <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid #999' } }}>
                          <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Total Deduction (B)</TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD(totalDeductionCum)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD(totalDeductionPrev)}</strong></TableCell>
                          <TableCell sx={computedCellSx}><strong>{fmtKWD(totalDeductionNet)}</strong></TableCell>
                        </TableRow>

                        {/* Payment (C = A - B) */}
                        <TableRow sx={{ '& td': { fontWeight: 700, backgroundColor: '#e8f5e9', borderTop: '2px solid #333' } }}>
                          <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Payment (C = A - B)</TableCell>
                          <TableCell sx={{ ...computedCellSx, backgroundColor: '#e8f5e9', color: paymentCum < 0 ? '#d32f2f' : undefined }}>
                            <strong>{fmtKWD(paymentCum)}</strong>
                          </TableCell>
                          <TableCell sx={{ ...computedCellSx, backgroundColor: '#e8f5e9' }}>
                            <strong>{fmtKWD(paymentPrev)}</strong>
                          </TableCell>
                          <TableCell sx={{ ...computedCellSx, backgroundColor: '#e8f5e9', color: paymentNet < 0 ? '#d32f2f' : undefined }}>
                            <strong>{fmtKWD(paymentNet)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Amount in words */}
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fafafa', border: '1px solid #ddd', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        <strong>Net Payment Amount in Words:</strong> {paymentWords}
                      </Typography>
                    </Box>
                  </Paper>
                  )}

                  {/* ─── Action Buttons ─── */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveDraft}
                        disabled={submitting || isReadOnly}
                      >
                        Save as Draft
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={handleSubmitToFinance}
                        disabled={submitting || isReadOnly}
                      >
                        Submit to Finance
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleGeneratePDF}
                        disabled={!selectedSubcontract}
                      >
                        Generate PDF
                      </Button>
                    </Stack>
                  </Paper>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* TAB 1: PAYMENT CERTIFICATE LIST                    */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Search"
              size="small"
              value={pcSearchQuery}
              onChange={(e) => setPcSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select value={pcListFilter} label="Status Filter" onChange={(e) => setPcListFilter(e.target.value)}>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="SubmittedToFinance">Submitted to Finance</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadPCList}>
              Refresh
            </Button>
          </Stack>

          {pcListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>PC Number</TableCell>
                  <TableCell sx={headCellSx}>Project</TableCell>
                  <TableCell sx={headCellSx}>Subcontract</TableCell>
                  <TableCell sx={headCellSx}>Payment Type</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Net Payment (KWD)</TableCell>
                  <TableCell sx={headCellSx}>Work Done Up To</TableCell>
                  <TableCell sx={headCellSx}>Created</TableCell>
                  <TableCell sx={headCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPCList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No payment certificates found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPCList.map((pc) => (
                    <TableRow
                      key={pc.id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f9ff' } }}
                      onClick={() => loadExistingPC(pc.id, pc.subcontract_id)}
                    >
                      <TableCell sx={cellSx}>{pc.pc_number}</TableCell>
                      <TableCell sx={cellSx}>{pc.project_code}</TableCell>
                      <TableCell sx={cellSx}>{pc.subcontract_number}</TableCell>
                      <TableCell sx={cellSx}>{pc.payment_type}</TableCell>
                      <TableCell sx={cellSx}>
                        <Chip
                          label={pc.status}
                          size="small"
                          color={pc.status === 'Draft' ? 'warning' : pc.status === 'SubmittedToFinance' ? 'info' : 'success'}
                        />
                      </TableCell>
                      <TableCell sx={rightCellSx}>{fmtKWD(pc.payment_cum)}</TableCell>
                      <TableCell sx={cellSx}>{pc.work_done_up_to}</TableCell>
                      <TableCell sx={cellSx}>{pc.created_at ? new Date(pc.created_at).toLocaleDateString() : ''}</TableCell>
                      <TableCell sx={cellSx}>
                        <Button size="small" variant="text" onClick={(e) => { e.stopPropagation(); loadExistingPC(pc.id, pc.subcontract_id); }}>
                          {pc.status === 'Draft' ? 'Edit' : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* PROJECT PICKER DIALOG                              */}
      {/* ═══════════════════════════════════════════════════ */}
      <ProjectChooseDialog
        open={projectPickerOpen}
        projects={projectOptions}
        loading={projectOptionsLoading}
        selectedCode={selectedProject}
        onClose={() => setProjectPickerOpen(false)}
        onSelect={(project) => {
          resetForm();
          setSelectedProject(project.code);
          setSelectedSubcontract(null);
        }}
      />
      <ActionStatusDialog
        {...actionStatus}
        onClose={() => setActionStatus((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
};

export default PaymentCertificate;
